import { ApolloClient } from "@apollo/client";
import { type CameraKitOrganization } from "../../../gql-generated/types";
import { OrganizationsRepository } from "./OrganizationsRepository";

export class OrganizationsService {
    public constructor(public cache: OrganizationsRepository) {
        if (!this.cache) {
            throw new Error("Cache should be provided");
        }
    }

    public static create(apolloClient: ApolloClient<any>) {
        return new OrganizationsService(new OrganizationsRepository(apolloClient));
    }

    public async switchOrganization(organization: Readonly<CameraKitOrganization>) {
        if (this.cache.getSelectedOrganization()?.id === organization.id) {
            return organization;
        }

        this.cache.setSelectedOrganizationId(organization);

        const stopRestoringSelectedOrganization = this.cache.apolloClient.onResetStore(() => {
            // LOAD-BEARING — do not remove. resetStore wipes the cache between active-query
            // refetches; this re-seeds the selected org *during* that window so any consumer
            // that reads it mid-reset (incl. refetched queries) still sees the route org.
            // The pre-reset write on line 21 and the post-reset write on line 35 are not a
            // substitute — they bracket the gap rather than fill it.
            this.cache.setSelectedOrganizationId(organization);
            return Promise.resolve();
        });

        try {
            await this.cache.apolloClient.resetStore();
        } finally {
            stopRestoringSelectedOrganization();
        }

        return this.cache.setSelectedOrganizationId(organization);
    }

    public selectIfValidOrganization(organizationId: string) {
        const organizations = this.cache.getOrganizations();
        const validOrganization = organizations?.find((org) => org?.id === organizationId) ?? organizations?.[0];
        this.cache.setSelectedOrganizationId(validOrganization as Readonly<CameraKitOrganization>);
    }

    public selectOrganizationSearch(organizationId: string) {
        const organizations = this.cache.searchOrganization(organizationId);

        this.cache.setSelectedOrganizationId(organizations?.[0] as Readonly<CameraKitOrganization>);
    }
}
